import * as THREE from 'three';
import OBJECT_NAMES from 'components/customer/RoomPlanner/constants/ObjectNameMeasurement';
import {PreviewPosition} from 'components/customer/Preview3D/types';
import {canShowLabelAndMeasurements} from 'components/customer/RoomPlanner/helpers/canShowLabelAndMeasurements';
import {
    preventLShapedSpriteOverlapByView,
    preventSpriteOverlapByView,
} from 'components/customer/RoomPlanner/helpers/preventSpriteOverlapByView';
import {buildHeightArrowsAndTextGroup} from 'components/customer/RoomPlanner/helpers/createRoomObjects';
import {
    elevationHeightsMatch,
    generateCabinetMeasurementData,
    generateEmptySpaceMeasurementData,
    getCabinetBoundingInfo,
    isCabinetClampedToWall,
    isWallHidden,
} from 'components/customer/RoomPlanner/helpers/measurement/measurementData';
import {generateMeasurementVisuals} from 'components/customer/RoomPlanner/helpers/measurement/renderMeasurements';
import {
    LShapedMeasurementGroups,
    MeasurementConfig,
    MeasurementData,
    RectangularMeasurementGroups,
    WallMeasurements,
} from 'components/customer/RoomPlanner/helpers/measurement/types';

/**
 * Recursively removes and disposes all measurement groups from a parent object
 * @param {THREE.Object3D} parent - The parent object to remove measurements from
 * @return {void}
 */
export const removeExistingMeasurements = (
    parent: THREE.Object3D<THREE.Object3DEventMap>
): void => {
    [...parent.children].reverse().forEach((child) => {
        if (
            child.name === 'emptySpaceMeasurementsGroup' ||
            child.name === 'multipleMeasurementsGroup' ||
            child.name === 'cabinetMeasurementsGroup' ||
            child.name === 'heightMeasurementGroup' ||
            (child.userData &&
                (child.userData.type === 'emptySpaceMeasurements' ||
                    child.userData.type === 'multipleMeasurements' ||
                    child.userData.type === 'heightMeasurements' ||
                    child.userData.isMeasurement === true))
        ) {
            parent.remove(child);
            // Dispose geometries and materials
            child.traverse((obj) => {
                if (obj instanceof THREE.Mesh || obj instanceof THREE.Line) {
                    const geometry = obj.geometry as
                        THREE.BufferGeometry | undefined;
                    if (geometry) {
                        geometry.dispose();
                    }
                    const material = obj.material as
                        THREE.Material | THREE.Material[] | undefined;
                    if (Array.isArray(material)) {
                        material.forEach((m) => {
                            if (m) {
                                m.dispose();
                            }
                        });
                    } else if (material) {
                        material.dispose();
                    }
                }
                if (obj instanceof THREE.Sprite && obj.material) {
                    if (obj.material.map) {
                        obj.material.map.dispose();
                    }
                    obj.material.dispose();
                }
            });
        }
        if (child.children && child.children.length > 0) {
            removeExistingMeasurements(child);
        }
    });
};

export const cleanupMeasurementData = (
    measurementData: MeasurementData[],
    adjustRightCabinets = false
) => {
    return measurementData.map((data) => {
        let xPosition = data.xPosition;
        if (adjustRightCabinets && data.currentDirection === 'right') {
            xPosition += data.actualWidth - 22.5;
        }
        return {
            ...data,
            xPosition,
        };
    });
};

/**
 * Elevation side walls draw a height arrow for every cabinet on the viewed
 * wall. Identical 720 labels then stack (and a filler bbox can span
 * floor-to-top while still labeled 720). Keep one row per labeled span.
 * @param {MeasurementData[]} measurementData - Cabinet height rows
 * @return {MeasurementData[]} Deduped rows
 */
export const dedupeElevationHeightMeasurements = (
    measurementData: MeasurementData[]
): MeasurementData[] => {
    const kept: MeasurementData[] = [];
    measurementData.forEach((item) => {
        const alreadyGrouped = kept.some((existing) =>
            elevationHeightsMatch(existing, item)
        );
        if (!alreadyGrouped) {
            kept.push(item);
        }
    });
    return kept;
};

export const prioritiseCabinetsOnWall = (
    measurementData: MeasurementData[],
    wall: THREE.Mesh
) => {
    let maxDistance = 0;
    const getDistanceToWall = (data: MeasurementData) => {
        const wallBox = new THREE.Box3().setFromObject(wall);
        const cabinetBox = new THREE.Box3().setFromObject(data.cabinet);
        let distanceToWall = 0;
        switch (wall.name) {
            case 'wall:left':
            case 'leftWidthWall':
                distanceToWall = Math.abs(wallBox.max.x - cabinetBox.min.x);
                break;
            case 'wall:front':
            case 'wall:back':
            case 'leftDepthWall':
                distanceToWall = Math.abs(wallBox.min.z - cabinetBox.max.z);
                break;
        }
        return distanceToWall;
    };
    measurementData.forEach((data) => {
        const distanceToWall = getDistanceToWall(data);
        if (distanceToWall > maxDistance) {
            maxDistance = distanceToWall;
        }
    });
    measurementData.forEach((data) => {
        const distanceToWall = getDistanceToWall(data);
        const normalizedDistance = 1 - distanceToWall / maxDistance;
        data.priority = Math.round(normalizedDistance * 10);
    });
};

/**
 * New optimized measurement setup using data-driven approach
 * Replaces the old setupRectangularMeasurements function
 *
 * PERFORMANCE OPTIMIZATIONS:
 * - Wall filtering: O(4n) → O(n) using Map indexing instead of 4 separate filter operations
 * - Cabinet caching: Directions and bounding info cached with lazy loading to avoid recalculation
 * - Reduced property access: Cached cabinet directions eliminate repeated userData.currentDirection lookups
 * - Bounding box reuse: getCabinetBoundingInfo results cached per cabinet to avoid expensive THREE.js calculations
 * - Early returns: Optimized filter functions return early when possible
 *
 * @param {React.MutableRefObject<THREE.Mesh[]>} wallRefs - Reference to wall mesh objects
 * @param {THREE.Group<THREE.Object3DEventMap>[] | undefined} cabinetGroupList - Array of cabinet group objects
 * @param {Function} handleArrowsAndGroup - Function to handle arrow and group creation
 * @param {Function} getGroupWidth - Function to get width, height, and depth of a group
 * @param {Object} rectangularDimension - Dimensions for rectangular room
 * @param {number} scale - Scale factor for measurements
 * @param {boolean} showCabinets - Whether to show cabinets
 * @param {boolean} showArrows - Whether to show measurement arrows
 * @param {THREE.Group<THREE.Object3DEventMap>} cabinetGroup - Current cabinet group being processed
 * @param {string} defaultLabelFontSize - Default font size for labels
 * @param {boolean} isForPrinting - Whether the view is for printing
 * @param {PreviewPosition} previewPosition - Current preview position
 * @param {string | null} hiddenWall - Comma-separated string of hidden wall IDs (optional)
 * @param {boolean} skipCleanup - Skip removing existing measurements (used when accumulating print views)
 * @return {RectangularMeasurementGroups | null} Measurement groups for the room type or null if setup fails
 */
export const setupOptimizedMeasurements = (
    wallRefs: React.MutableRefObject<THREE.Mesh[]>,
    cabinetGroupList: THREE.Group<THREE.Object3DEventMap>[] | undefined,
    handleArrowsAndGroup: (
        arrows: (THREE.ArrowHelper | THREE.SpriteMaterial)[],
        arrowGroup?: THREE.Group
    ) => void,
    getGroupWidth: (group: THREE.Group<THREE.Object3DEventMap>) => {
        width: number;
        height: number;
        depth: number;
    },
    rectangularDimension: {
        length: number;
        width: number;
        height: number;
    },
    scale: number,
    showCabinets: boolean,
    showArrows: boolean,
    cabinetGroup: THREE.Group<THREE.Object3DEventMap>,
    defaultLabelFontSize: string,
    isForPrinting: boolean,
    previewPosition: PreviewPosition,
    hiddenWall?: string | null,
    skipCleanup = false
): RectangularMeasurementGroups => {
    const sets = canShowLabelAndMeasurements(isForPrinting, previewPosition);
    // OPTIMIZATION: Pre-index walls by ID (O(n) instead of O(4n))
    const wallMap = new Map<string, THREE.Mesh>();
    for (const wall of wallRefs.current) {
        if (wall.userData.id) {
            wallMap.set(wall.userData.id as string, wall);
        }
    }

    const walls = {
        backWall: wallMap.get(OBJECT_NAMES.BACK_WALL),
        frontWall: wallMap.get(OBJECT_NAMES.FRONT_WALL),
        leftWall: wallMap.get(OBJECT_NAMES.LEFT_WALL),
        rightWall: wallMap.get(OBJECT_NAMES.RIGHT_WALL),
    };

    // Clean up all walls (skipped when accumulating print views so earlier
    // views' tagged groups survive)
    if (!skipCleanup) {
        Object.values(walls).forEach((wall) => {
            if (wall) removeExistingMeasurements(wall);
        });
    }

    // OPTIMIZATION: Pre-process cabinets - cache directions for faster filtering
    const cabinetCache = new Map<
        THREE.Group<THREE.Object3DEventMap>,
        {
            direction: string;
        }
    >();

    const preprocessCabinets = () => {
        if (!cabinetGroupList) return;

        for (const cabinet of cabinetGroupList) {
            const direction =
                (cabinet?.userData?.currentDirection as string) || 'unknown';
            cabinetCache.set(cabinet, {
                direction,
            });
        }
    };

    // Initialize cabinet cache
    preprocessCabinets();

    // OPTIMIZATION: Helper function to get cached direction
    const getCachedDirection = (
        cabinet: THREE.Group<THREE.Object3DEventMap>
    ): string => {
        const cached = cabinetCache.get(cabinet);
        return (
            cached?.direction ||
            (cabinet?.userData?.currentDirection as string) ||
            'unknown'
        );
    };

    // Helper function to create measurements for a wall
    const createWallMeasurements = (
        wall: THREE.Mesh,
        wallId: string,
        wallType: keyof typeof sets,
        cabinetFilter: (
            cabinet: THREE.Group<THREE.Object3DEventMap>
        ) => boolean,
        config: MeasurementConfig
    ) => {
        // Check if wall should be hidden (skip measurement creation when not printing)
        // When printing or in TOP/TOP_WITHOUT_EDITING views, skip wall hider logic entirely
        const shouldSkipWallHider =
            isForPrinting ||
            previewPosition === 'TOP' ||
            previewPosition === 'TOP_WITHOUT_EDITING';
        if (
            !shouldSkipWallHider &&
            isWallHidden(wallId, hiddenWall, 'RECTANGULAR', isForPrinting)
        ) {
            return {cabinetMeasurements: null, emptySpaceMeasurements: null};
        }

        const isPlanView = previewPosition === 'TOP' && isForPrinting;
        const isFrontElevation = previewPosition === 'FRONT' && isForPrinting;
        const isLeftElevation =
            previewPosition === 'RIGHT_LEVELED' && isForPrinting;
        const isRightElevation =
            previewPosition === 'LEFT_LEVELED' && isForPrinting;

        // Filter cabinets: only those that pass the filter and do NOT overlap the wall
        const filteredCabinets =
            cabinetGroupList?.filter((cabinet) => cabinetFilter(cabinet)) || [];

        let wallMeasurementsGroup: THREE.Group | null = null;
        let wallEmptySpaceGroup: THREE.Group | null = null;

        if (filteredCabinets.length > 0 && showCabinets) {
            const filteredIds = filteredCabinets.map((item) => {
                return item.userData.id as string;
            });
            const excludedCabinets = cabinetGroupList?.filter((group) => {
                return !filteredIds.includes(group.userData.id as string);
            });
            // Generate cabinet measurement data
            let cabinetMeasurementData = generateCabinetMeasurementData(
                filteredCabinets,
                config,
                'cabinet',
                cabinetGroup
            );

            if (
                isPlanView ||
                (isFrontElevation && wallId === OBJECT_NAMES.BACK_WALL) ||
                (isLeftElevation && wallId === OBJECT_NAMES.LEFT_WALL) ||
                (isRightElevation && wallId === OBJECT_NAMES.RIGHT_WALL)
            ) {
                cabinetMeasurementData = cleanupMeasurementData(
                    cabinetMeasurementData
                );
            }

            if (
                (isFrontElevation && wallId === OBJECT_NAMES.LEFT_WALL) ||
                (isLeftElevation && wallId === OBJECT_NAMES.FRONT_WALL) ||
                (isRightElevation && wallId === OBJECT_NAMES.BACK_WALL)
            ) {
                cabinetMeasurementData = dedupeElevationHeightMeasurements(
                    cabinetMeasurementData
                );
                prioritiseCabinetsOnWall(cabinetMeasurementData, wall);
            }

            // Generate empty space measurement data
            const emptySpaceMeasurementData = generateEmptySpaceMeasurementData(
                filteredCabinets,
                config,
                cabinetMeasurementData
            );

            if (
                (isFrontElevation && wallId === OBJECT_NAMES.LEFT_WALL) ||
                (isLeftElevation && wallId === OBJECT_NAMES.FRONT_WALL) ||
                (isRightElevation && wallId === OBJECT_NAMES.BACK_WALL)
            ) {
                // Create cabinet measurements group
                wallMeasurementsGroup = new THREE.Group();
                wallMeasurementsGroup.name = 'multipleMeasurementsGroup';
                wallMeasurementsGroup.userData.type = 'multipleMeasurements';
                wallMeasurementsGroup.userData.id = wallId;
                wallMeasurementsGroup.userData.excludedCabinets =
                    excludedCabinets;

                // Generate cabinet measurement visuals
                generateMeasurementVisuals(
                    [...cabinetMeasurementData, ...emptySpaceMeasurementData],
                    config,
                    wallMeasurementsGroup,
                    handleArrowsAndGroup as (
                        arrows: (
                            | THREE.ArrowHelper
                            | THREE.SpriteMaterial
                            | THREE.Line
                        )[],
                        arrowGroup?: THREE.Group
                    ) => void
                );

                wall.add(wallMeasurementsGroup);
            } else {
                // Create cabinet measurements group
                wallMeasurementsGroup = new THREE.Group();
                wallMeasurementsGroup.name = 'multipleMeasurementsGroup';
                wallMeasurementsGroup.userData.type = 'multipleMeasurements';
                wallMeasurementsGroup.userData.id = wallId;

                // Generate cabinet measurement visuals
                generateMeasurementVisuals(
                    cabinetMeasurementData,
                    config,
                    wallMeasurementsGroup,
                    handleArrowsAndGroup as (
                        arrows: (
                            | THREE.ArrowHelper
                            | THREE.SpriteMaterial
                            | THREE.Line
                        )[],
                        arrowGroup?: THREE.Group
                    ) => void
                );

                wall.add(wallMeasurementsGroup);

                // Create empty space measurements group
                wallEmptySpaceGroup = new THREE.Group();
                wallEmptySpaceGroup.name = 'emptySpaceMeasurementsGroup';
                wallEmptySpaceGroup.userData.type = 'emptySpaceMeasurements';

                // Generate empty space measurement visuals
                generateMeasurementVisuals(
                    emptySpaceMeasurementData,
                    config,
                    wallEmptySpaceGroup,
                    handleArrowsAndGroup as (
                        arrows: (
                            | THREE.ArrowHelper
                            | THREE.SpriteMaterial
                            | THREE.Line
                        )[],
                        arrowGroup?: THREE.Group
                    ) => void
                );

                wall.add(wallEmptySpaceGroup);
            }
        } else {
            // No cabinets - create empty wall measurement
            const emptyWallMeasurementData = generateEmptySpaceMeasurementData(
                [],
                config
            );

            wallEmptySpaceGroup = new THREE.Group();
            wallEmptySpaceGroup.name = 'emptySpaceMeasurementsGroup';
            wallEmptySpaceGroup.userData.type = 'emptySpaceMeasurements';

            generateMeasurementVisuals(
                emptyWallMeasurementData,
                config,
                wallEmptySpaceGroup,
                handleArrowsAndGroup as (
                    arrows: (
                        THREE.ArrowHelper | THREE.SpriteMaterial | THREE.Line
                    )[],
                    arrowGroup?: THREE.Group
                ) => void
            );

            wall.add(wallEmptySpaceGroup);
        }

        const isLeftElevationHeight =
            wallId === OBJECT_NAMES.LEFT_WALL && isLeftElevation;
        const isRightElevationHeight =
            wallId === OBJECT_NAMES.RIGHT_WALL && isRightElevation;

        // Add height measurement arrows/text only for the back wall
        let heightMeasurementGroup: THREE.Group | null = null;
        if (
            (wallId === OBJECT_NAMES.BACK_WALL &&
                !isPlanView &&
                !isRightElevation) ||
            isLeftElevationHeight ||
            isRightElevationHeight
        ) {
            const {group, arrowObjects} = buildHeightArrowsAndTextGroup(
                config.wallHeight - 20,
                config.scale,
                wallId,
                config.defaultLabelFontSize,
                isLeftElevationHeight ? {x: 20} : undefined
            );
            heightMeasurementGroup = group;
            handleArrowsAndGroup(arrowObjects, group);

            if (isLeftElevationHeight) {
                group.translateX(config.roomWidth * config.scale + 65);
            }

            // Keep wall-height on the wall itself. Nesting it under empty-space
            // hid it from the PDF camera fit (that box skips empty-space overlays)
            // so the 2800 label was cropped off the elevation.
            wall.add(group);
        }

        // Set visibility based on showArrows AND wall-specific visibility from sets
        if (wallMeasurementsGroup?.userData?.updateVisibility) {
            (
                wallMeasurementsGroup.userData.updateVisibility as (
                    show: boolean
                ) => void
            )(
                // eslint-disable-next-line security/detect-object-injection -- wallType is from a predefined enum, not user input
                showArrows && sets[wallType]
            );
        }
        if (wallEmptySpaceGroup?.userData?.updateVisibility) {
            (
                wallEmptySpaceGroup.userData.updateVisibility as (
                    show: boolean
                ) => void
            )(
                // eslint-disable-next-line security/detect-object-injection -- wallType is from a predefined enum, not user input
                showArrows && sets[wallType]
            );
        }
        if (heightMeasurementGroup?.userData?.updateVisibility) {
            (
                heightMeasurementGroup.userData.updateVisibility as (
                    show: boolean
                ) => void
            )(
                // eslint-disable-next-line security/detect-object-injection -- wallType is from a predefined enum, not user input
                showArrows && sets[wallType]
            );
        }

        if (isForPrinting) {
            // Tag groups per print view so the PDF snapshot loop can toggle
            // visibility per stage instead of rebuilding measurements.
            [
                wallMeasurementsGroup,
                wallEmptySpaceGroup,
                heightMeasurementGroup,
            ].forEach((measurementGroup) => {
                if (!measurementGroup) {
                    return;
                }
                measurementGroup.userData.printView = previewPosition;
                measurementGroup.userData.printVisible =
                    // eslint-disable-next-line security/detect-object-injection -- wallType is from a predefined enum, not user input
                    showArrows && sets[wallType];
            });
        }

        return {
            cabinetMeasurements: wallMeasurementsGroup,
            emptySpaceMeasurements: wallEmptySpaceGroup,
        };
    };

    // Create measurements for each wall
    const result: RectangularMeasurementGroups = {
        backWall: {
            cabinetMeasurements: null,
            emptySpaceMeasurements: null,
        },
        frontWall: null,
        leftWall: null,
        rightWall: null,
    };

    const isPlanView = isForPrinting && previewPosition === 'TOP';
    const isFrontElevation = isForPrinting && previewPosition === 'FRONT';
    const isLeftElevation =
        isForPrinting && previewPosition === 'RIGHT_LEVELED';
    const isRightElevation =
        isForPrinting && previewPosition === 'LEFT_LEVELED';

    // Back Wall - cabinets facing front (currentDirection !== 'back')
    if (walls.backWall) {
        const backWallConfig: MeasurementConfig = {
            wallHeight: rectangularDimension.height * scale + 20,
            defaultLabelFontSize,
            roomWidth: rectangularDimension.width,
            roomLength: rectangularDimension.length,
            scale,
            wallRefId: OBJECT_NAMES.BACK_WALL,
            showArrows,
            isForPrinting,
            previewPosition,
        };

        result.backWall = createWallMeasurements(
            walls.backWall,
            OBJECT_NAMES.BACK_WALL,
            'backWall',
            (cabinet) => {
                if (isPlanView) {
                    const {backWall} = walls;
                    const wallBox = new THREE.Box3().setFromObject(backWall);
                    const cabinetBox = new THREE.Box3().setFromObject(cabinet);

                    const minDistance = wallBox.max.z - cabinetBox.min.z;
                    const maxDistance = wallBox.max.z - cabinetBox.max.z;
                    const wallHalf = (rectangularDimension.length * scale) / 2;
                    return (
                        !(Math.abs(minDistance) > wallHalf) &&
                        !(Math.abs(maxDistance) > wallHalf)
                    );
                } else if (isFrontElevation) {
                    return (
                        isCabinetClampedToWall(cabinet, walls.backWall, 'z') &&
                        (getCachedDirection(cabinet) == 'front' ||
                            (getCachedDirection(cabinet) == 'right' &&
                                !!cabinet.userData?.isLShapedProduct))
                    );
                } else if (isRightElevation) {
                    return (
                        isCabinetClampedToWall(cabinet, walls.rightWall, 'x') &&
                        (getCachedDirection(cabinet) == 'right' ||
                            (getCachedDirection(cabinet) == 'back' &&
                                !!cabinet.userData?.isLShapedProduct))
                    );
                }
                return (
                    getCachedDirection(cabinet) !== 'back' &&
                    isCabinetClampedToWall(cabinet, walls.backWall, 'z')
                );
            },
            backWallConfig
        );
    }

    // Front Wall - cabinets facing back (currentDirection === 'back')
    if (walls.frontWall) {
        const frontWallConfig: MeasurementConfig = {
            wallHeight: rectangularDimension.height * scale + 20,
            defaultLabelFontSize,
            roomWidth: rectangularDimension.width,
            roomLength: rectangularDimension.length,
            scale,
            wallRefId: OBJECT_NAMES.FRONT_WALL,
            showArrows,
            isForPrinting,
            previewPosition,
        };

        result.frontWall = createWallMeasurements(
            walls.frontWall,
            OBJECT_NAMES.FRONT_WALL,
            'frontWall',
            (cabinet) => {
                if (isPlanView) {
                    const {frontWall} = walls;
                    const wallBox = new THREE.Box3().setFromObject(frontWall);
                    const cabinetBox = new THREE.Box3().setFromObject(cabinet);
                    const distance = wallBox.min.z - cabinetBox.max.z;
                    const wallHalf = (rectangularDimension.length * scale) / 2;
                    return !(Math.abs(distance) > wallHalf);
                } else if (isLeftElevation) {
                    return (
                        isCabinetClampedToWall(cabinet, walls.leftWall, 'x') &&
                        (getCachedDirection(cabinet) == 'left' ||
                            (getCachedDirection(cabinet) == 'front' &&
                                !!cabinet.userData?.isLShapedProduct))
                    );
                }
                return (
                    getCachedDirection(cabinet) !== 'front' &&
                    isCabinetClampedToWall(cabinet, walls.frontWall, 'z')
                );
            },
            frontWallConfig
        );
    }

    // Left Wall - cabinets facing right (currentDirection === 'right')
    if (walls.leftWall) {
        const leftWallConfig: MeasurementConfig = {
            wallHeight: rectangularDimension.height * scale + 20,
            defaultLabelFontSize,
            roomWidth: rectangularDimension.length, // Use length for left/right walls
            roomLength: rectangularDimension.length,
            scale,
            wallRefId: OBJECT_NAMES.LEFT_WALL,
            showArrows,
            isForPrinting,
            previewPosition,
        };

        result.leftWall = createWallMeasurements(
            walls.leftWall,
            OBJECT_NAMES.LEFT_WALL,
            'leftWall',
            (cabinet) => {
                if (isPlanView) {
                    const {leftWall} = walls;
                    const wallBox = new THREE.Box3().setFromObject(leftWall);
                    const cabinetBox = new THREE.Box3().setFromObject(cabinet);

                    const minDistance = wallBox.max.x - cabinetBox.min.x;
                    const maxDistance = wallBox.max.x - cabinetBox.max.x;

                    const wallHalf = (rectangularDimension.width * scale) / 2;
                    return (
                        !(Math.abs(minDistance) > wallHalf) &&
                        !(Math.abs(maxDistance) > wallHalf)
                    );
                } else if (isFrontElevation) {
                    return (
                        isCabinetClampedToWall(cabinet, walls.backWall, 'z') &&
                        (getCachedDirection(cabinet) == 'front' ||
                            (getCachedDirection(cabinet) == 'right' &&
                                !!cabinet.userData?.isLShapedProduct))
                    );
                } else if (isLeftElevation) {
                    return (
                        isCabinetClampedToWall(cabinet, walls.leftWall, 'x') &&
                        (getCachedDirection(cabinet) == 'left' ||
                            (getCachedDirection(cabinet) == 'front' &&
                                !!cabinet.userData?.isLShapedProduct))
                    );
                }

                return (
                    getCachedDirection(cabinet) !== 'right' &&
                    isCabinetClampedToWall(cabinet, walls.leftWall, 'x')
                );
            },
            leftWallConfig
        );
    }

    // Right Wall - cabinets facing left (currentDirection === 'left')
    if (walls.rightWall) {
        const rightWallConfig: MeasurementConfig = {
            wallHeight: rectangularDimension.height * scale + 20,
            defaultLabelFontSize,
            roomWidth: rectangularDimension.length, // Use length for left/right walls
            roomLength: rectangularDimension.length, // Fixed: should be consistent with left wall
            scale,
            wallRefId: OBJECT_NAMES.RIGHT_WALL,
            showArrows,
            isForPrinting,
            previewPosition,
        };

        result.rightWall = createWallMeasurements(
            walls.rightWall,
            OBJECT_NAMES.RIGHT_WALL,
            'rightWall',
            (cabinet) => {
                if (isPlanView) {
                    const {rightWall} = walls;
                    const wallBox = new THREE.Box3().setFromObject(rightWall);
                    const cabinetBox = new THREE.Box3().setFromObject(cabinet);

                    const distance = wallBox.min.x - cabinetBox.max.x;
                    const wallHalf = (rectangularDimension.width * scale) / 2;
                    return !(Math.abs(distance) > wallHalf);
                } else if (isRightElevation) {
                    return (
                        isCabinetClampedToWall(cabinet, walls.rightWall, 'x') &&
                        (getCachedDirection(cabinet) == 'right' ||
                            (getCachedDirection(cabinet) == 'back' &&
                                !!cabinet.userData?.isLShapedProduct))
                    );
                }
                return (
                    getCachedDirection(cabinet) !== 'left' &&
                    isCabinetClampedToWall(cabinet, walls.rightWall, 'x')
                );
            },
            rightWallConfig
        );
    }

    return result;
};

/**
 * Sets up measurements for L-shaped room walls and cabinets
 *
 * PERFORMANCE OPTIMIZATIONS:
 * - Wall filtering: O(6n) → O(n) using Map indexing instead of 6 separate filter operations
 * - Cabinet caching: Directions and bounding info cached with lazy loading to avoid recalculation
 * - Reduced property access: Cached cabinet directions eliminate repeated userData.currentDirection lookups
 * - Bounding box reuse: getCabinetBoundingInfo results cached per cabinet to avoid expensive THREE.js calculations
 * - Early returns: Optimized filter functions return early when possible
 *
 * @param {React.MutableRefObject<THREE.Mesh[]>} wallRefs - Reference to wall mesh objects
 * @param {THREE.Group<THREE.Object3DEventMap>[] | undefined} cabinetGroupList - Array of cabinet group objects
 * @param {Function} handleArrowsAndGroup - Function to handle arrow and group creation
 * @param {Function} getGroupWidth - Function to get width, height, and depth of a group
 * @param {Object} lShapedDimension - Dimensions for L-shaped room
 * @param {number} scale - Scale factor for measurements
 * @param {boolean} showCabinets - Whether to show cabinets
 * @param {boolean} showArrows - Whether to show measurement arrows
 * @param {THREE.Group<THREE.Object3DEventMap>} cabinetGroup - Current cabinet group being processed
 * @param {string} defaultLabelFontSize - Default font size for labels
 * @param {boolean} isForPrinting - Whether the view is for printing (affects visibility rules)
 * @param {PreviewPosition} previewPosition - Current preview position
 * @param {string | null} hiddenWall - Comma-separated string of hidden wall IDs (optional)
 * @param {boolean} skipCleanup - Skip removing existing measurements (used when accumulating print views)
 * @return {LShapedMeasurementGroups | null} Measurement groups for the L-shaped room or null if setup fails
 */
export const setupLShapedMeasurements = (
    wallRefs: React.MutableRefObject<THREE.Mesh[]>,
    cabinetGroupList: THREE.Group<THREE.Object3DEventMap>[] | undefined,
    handleArrowsAndGroup: (
        arrows: (THREE.ArrowHelper | THREE.SpriteMaterial)[],
        arrowGroup?: THREE.Group
    ) => void,
    getGroupWidth: (group: THREE.Group<THREE.Object3DEventMap>) => {
        width: number;
        height: number;
        depth: number;
    },
    lShapedDimension: {
        leftWidth: number;
        rightWidth: number;
        leftDepth: number;
        rightDepth: number;
        height: number;
    },
    scale: number,
    showCabinets: boolean,
    showArrows: boolean,
    cabinetGroup: THREE.Group<THREE.Object3DEventMap>,
    defaultLabelFontSize: string,
    isForPrinting: boolean,
    previewPosition: PreviewPosition,
    hiddenWall?: string | null,
    skipCleanup = false
): LShapedMeasurementGroups => {
    const sets = canShowLabelAndMeasurements(isForPrinting, previewPosition);
    // OPTIMIZATION: Pre-index walls by ID (O(n) instead of O(6n))
    const wallMap = new Map<string, THREE.Mesh>();
    for (const wall of wallRefs.current) {
        if (wall.userData.id) {
            wallMap.set(wall.userData.id as string, wall);
        }
    }

    const walls = {
        leftWidthWall: wallMap.get(OBJECT_NAMES.LEFT_WIDTH_WALL),
        leftWidth2Wall: wallMap.get(OBJECT_NAMES.LEFT_WIDTH2_WALL),
        rightWidthWall: wallMap.get(OBJECT_NAMES.RIGHT_WIDTH_WALL),
        rightWidth2Wall: wallMap.get(OBJECT_NAMES.RIGHT_WIDTH2_WALL),
        leftDepthWall: wallMap.get(OBJECT_NAMES.LEFT_DEPTH_WALL),
        rightDepthWall: wallMap.get(OBJECT_NAMES.RIGHT_DEPTH_WALL),
    };

    // Clean up all walls (skipped when accumulating print views so earlier
    // views' tagged groups survive)
    if (!skipCleanup) {
        Object.values(walls).forEach((wall) => {
            if (wall) removeExistingMeasurements(wall);
        });
    }

    // OPTIMIZATION: Pre-process cabinets - cache directions and bounding info
    const cabinetCache = new Map<
        THREE.Group<THREE.Object3DEventMap>,
        {
            direction: string;
            boundingInfo: ReturnType<typeof getCabinetBoundingInfo> | null;
        }
    >();

    const preprocessCabinets = () => {
        if (!cabinetGroupList) return;

        for (const cabinet of cabinetGroupList) {
            const direction =
                (cabinet?.userData?.currentDirection as string) || 'unknown';
            cabinetCache.set(cabinet, {
                direction,
                boundingInfo: null, // Lazy load when needed
            });
        }
    };

    // OPTIMIZATION: Get cached bounding info with lazy loading
    const getCachedBoundingInfo = (
        cabinet: THREE.Group<THREE.Object3DEventMap>,
        config: MeasurementConfig
    ) => {
        const cached = cabinetCache.get(cabinet);
        if (!cached) return getCabinetBoundingInfo(cabinet, config);

        if (!cached.boundingInfo) {
            cached.boundingInfo = getCabinetBoundingInfo(cabinet, config);
        }
        return cached.boundingInfo;
    };

    // Initialize cabinet cache
    preprocessCabinets();

    // OPTIMIZATION: Helper function to get cached direction
    const getCachedDirection = (
        cabinet: THREE.Group<THREE.Object3DEventMap>
    ): string => {
        const cached = cabinetCache.get(cabinet);
        return (
            cached?.direction ||
            (cabinet?.userData?.currentDirection as string) ||
            'unknown'
        );
    };

    // Helper function to create measurements for a wall
    const createWallMeasurements = (
        wall: THREE.Mesh,
        wallId: string,
        wallType: keyof typeof sets,
        cabinetFilter: (
            cabinet: THREE.Group<THREE.Object3DEventMap>
        ) => boolean,
        config: MeasurementConfig
    ) => {
        // Check if wall should be hidden (skip measurement creation when not printing)
        // When printing or in TOP/TOP_WITHOUT_EDITING views, skip wall hider logic entirely
        const shouldSkipWallHider =
            isForPrinting ||
            previewPosition === 'TOP' ||
            previewPosition === 'TOP_WITHOUT_EDITING';
        if (
            !shouldSkipWallHider &&
            isWallHidden(wallId, hiddenWall, 'LSHAPED', isForPrinting)
        ) {
            return {cabinetMeasurements: null, emptySpaceMeasurements: null};
        }

        // Filter cabinets: only those that pass the filter and do NOT overlap the wall
        const filteredCabinets =
            cabinetGroupList?.filter((cabinet) => cabinetFilter(cabinet)) || [];

        let wallMeasurementsGroup: THREE.Group | null = null;
        let wallEmptySpaceGroup: THREE.Group | null = null;

        const isPlanView = previewPosition === 'TOP' && isForPrinting;
        const isFrontElevation = isForPrinting && previewPosition === 'FRONT';
        const isLeftElevation =
            isForPrinting && previewPosition === 'RIGHT_LEVELED';
        const isRightElevation =
            isForPrinting && previewPosition === 'LEFT_LEVELED';

        // In plan-view printing the leftWidth2Wall is a small inner notch
        // segment. A wrongly-detected cabinet inflates its empty-space gap and
        // prints an oversized length, so force the empty-space-only path which
        // sizes the measurement to the wall's actual notch length
        // (config.roomWidth = leftWidth - rightDepth).
        const forceEmptyOnly =
            isPlanView && wallId === OBJECT_NAMES.LEFT_WIDTH2_WALL;

        if (filteredCabinets.length > 0 && !forceEmptyOnly && showCabinets) {
            const filteredIds = filteredCabinets.map((item) => {
                return item.userData.id as string;
            });
            const excludedCabinets = cabinetGroupList?.filter((group) => {
                return !filteredIds.includes(group.userData.id as string);
            });
            // Generate cabinet measurement data
            let cabinetMeasurementData = generateCabinetMeasurementData(
                filteredCabinets,
                config,
                'cabinet',
                cabinetGroup
            );

            if (
                isPlanView ||
                (isFrontElevation &&
                    wallId === OBJECT_NAMES.RIGHT_WIDTH_WALL) ||
                (isLeftElevation && wallId === OBJECT_NAMES.LEFT_WIDTH_WALL) ||
                (isRightElevation &&
                    [
                        OBJECT_NAMES.RIGHT_DEPTH_WALL,
                        OBJECT_NAMES.LEFT_WIDTH2_WALL,
                    ].includes(wallId))
            ) {
                cabinetMeasurementData = cleanupMeasurementData(
                    cabinetMeasurementData,
                    wallId === OBJECT_NAMES.LEFT_WIDTH_WALL
                );
            }

            if (
                (isFrontElevation && wallId === OBJECT_NAMES.LEFT_WIDTH_WALL) ||
                (isLeftElevation && wallId === OBJECT_NAMES.LEFT_DEPTH_WALL) ||
                (isRightElevation && wallId === OBJECT_NAMES.RIGHT_WIDTH_WALL)
            ) {
                cabinetMeasurementData = dedupeElevationHeightMeasurements(
                    cabinetMeasurementData
                );
                prioritiseCabinetsOnWall(cabinetMeasurementData, wall);
            }

            // Generate empty space measurement data
            const emptySpaceMeasurementData = generateEmptySpaceMeasurementData(
                filteredCabinets,
                config,
                cabinetMeasurementData
            );
            if (
                (isFrontElevation && wallId === OBJECT_NAMES.LEFT_WIDTH_WALL) ||
                (isLeftElevation && wallId === OBJECT_NAMES.LEFT_DEPTH_WALL) ||
                (isRightElevation && wallId === OBJECT_NAMES.RIGHT_WIDTH_WALL)
            ) {
                // Create cabinet measurements group
                wallMeasurementsGroup = new THREE.Group();
                wallMeasurementsGroup.name = 'multipleMeasurementsGroup';
                wallMeasurementsGroup.userData.type = 'multipleMeasurements';
                wallMeasurementsGroup.userData.id = wallId;
                wallMeasurementsGroup.userData.excludedCabinets =
                    excludedCabinets;

                // Generate cabinet measurement visuals
                generateMeasurementVisuals(
                    [...cabinetMeasurementData, ...emptySpaceMeasurementData],
                    config,
                    wallMeasurementsGroup,
                    handleArrowsAndGroup as (
                        arrows: (
                            | THREE.ArrowHelper
                            | THREE.SpriteMaterial
                            | THREE.Line
                        )[],
                        arrowGroup?: THREE.Group
                    ) => void
                );

                wall.add(wallMeasurementsGroup);
            } else {
                // Create cabinet measurements group
                wallMeasurementsGroup = new THREE.Group();
                wallMeasurementsGroup.name = 'multipleMeasurementsGroup';
                wallMeasurementsGroup.userData.type = 'multipleMeasurements';
                wallMeasurementsGroup.userData.id = wallId;

                // Generate cabinet measurement visuals
                generateMeasurementVisuals(
                    cabinetMeasurementData,
                    config,
                    wallMeasurementsGroup,
                    handleArrowsAndGroup as (
                        arrows: (
                            | THREE.ArrowHelper
                            | THREE.SpriteMaterial
                            | THREE.Line
                        )[],
                        arrowGroup?: THREE.Group
                    ) => void
                );

                wall.add(wallMeasurementsGroup);

                // Create empty space measurements group
                wallEmptySpaceGroup = new THREE.Group();
                wallEmptySpaceGroup.name = 'emptySpaceMeasurementsGroup';
                wallEmptySpaceGroup.userData.type = 'emptySpaceMeasurements';

                // Generate empty space measurement visuals
                generateMeasurementVisuals(
                    emptySpaceMeasurementData,
                    config,
                    wallEmptySpaceGroup,
                    handleArrowsAndGroup as (
                        arrows: (
                            | THREE.ArrowHelper
                            | THREE.SpriteMaterial
                            | THREE.Line
                        )[],
                        arrowGroup?: THREE.Group
                    ) => void
                );

                wall.add(wallEmptySpaceGroup);
            }
        } else {
            // No cabinets - create empty wall measurement
            const emptyWallMeasurementData = generateEmptySpaceMeasurementData(
                [],
                config
            );

            wallEmptySpaceGroup = new THREE.Group();
            wallEmptySpaceGroup.name = 'emptySpaceMeasurementsGroup';
            wallEmptySpaceGroup.userData.type = 'emptySpaceMeasurements';

            generateMeasurementVisuals(
                emptyWallMeasurementData,
                config,
                wallEmptySpaceGroup,
                handleArrowsAndGroup as (
                    arrows: (
                        THREE.ArrowHelper | THREE.SpriteMaterial | THREE.Line
                    )[],
                    arrowGroup?: THREE.Group
                ) => void
            );

            wall.add(wallEmptySpaceGroup);
        }

        const isLeftElevationHeight =
            wallId === OBJECT_NAMES.LEFT_WIDTH_WALL && isLeftElevation;
        const isRightElevationHeight =
            wallId === OBJECT_NAMES.RIGHT_DEPTH_WALL && isRightElevation;
        // Add height measurement arrows/text only for the back wall
        let heightMeasurementGroup: THREE.Group | null = null;
        if (
            (wallId === OBJECT_NAMES.RIGHT_WIDTH_WALL &&
                !isPlanView &&
                !isRightElevation) ||
            isLeftElevationHeight ||
            isRightElevationHeight
        ) {
            const {group, arrowObjects} = buildHeightArrowsAndTextGroup(
                config.wallHeight - 20,
                config.scale,
                wallId,
                config.defaultLabelFontSize,
                isLeftElevationHeight || isRightElevationHeight
                    ? {x: 20}
                    : undefined
            );
            heightMeasurementGroup = group;
            handleArrowsAndGroup(arrowObjects, group);

            if (isLeftElevationHeight || isRightElevationHeight) {
                group.translateX(config.leftWidth * config.scale + 65);
            }

            // Keep wall-height on the wall itself. Nesting it under empty-space
            // hid it from the PDF camera fit (that box skips empty-space overlays)
            // so the height label was cropped off the elevation.
            wall.add(group);
        }

        // Set visibility based on showArrows AND wall-specific visibility from sets
        if (wallMeasurementsGroup?.userData?.updateVisibility) {
            (
                wallMeasurementsGroup.userData.updateVisibility as (
                    show: boolean
                ) => void
            )(
                // eslint-disable-next-line security/detect-object-injection -- wallType is from a predefined enum, not user input
                showArrows && sets[wallType]
            );
        }
        if (wallEmptySpaceGroup?.userData?.updateVisibility) {
            (
                wallEmptySpaceGroup.userData.updateVisibility as (
                    show: boolean
                ) => void
            )(
                // eslint-disable-next-line security/detect-object-injection -- wallType is from a predefined enum, not user input
                showArrows && sets[wallType]
            );
        }

        if (heightMeasurementGroup?.userData?.updateVisibility) {
            (
                heightMeasurementGroup.userData.updateVisibility as (
                    show: boolean
                ) => void
            )(
                // eslint-disable-next-line security/detect-object-injection -- wallType is from a predefined enum, not user input
                showArrows && sets[wallType]
            );
        }

        if (isForPrinting) {
            // Tag groups per print view so the PDF snapshot loop can toggle
            // visibility per stage instead of rebuilding measurements.
            [
                wallMeasurementsGroup,
                wallEmptySpaceGroup,
                heightMeasurementGroup,
            ].forEach((measurementGroup) => {
                if (!measurementGroup) {
                    return;
                }
                measurementGroup.userData.printView = previewPosition;
                measurementGroup.userData.printVisible =
                    // eslint-disable-next-line security/detect-object-injection -- wallType is from a predefined enum, not user input
                    showArrows && sets[wallType];
            });
        }

        return {
            cabinetMeasurements: wallMeasurementsGroup,
            emptySpaceMeasurements: wallEmptySpaceGroup,
        };
    };

    const isPlanView = isForPrinting && previewPosition === 'TOP';
    const isFrontElevation = isForPrinting && previewPosition === 'FRONT';
    const isLeftElevation =
        isForPrinting && previewPosition === 'RIGHT_LEVELED';
    const isRightElevation =
        isForPrinting && previewPosition === 'LEFT_LEVELED';

    const result: LShapedMeasurementGroups = {
        leftWidthWall: {
            cabinetMeasurements: null,
            emptySpaceMeasurements: null,
        },
        leftWidth2Wall: {
            cabinetMeasurements: null,
            emptySpaceMeasurements: null,
        },
        rightWidthWall: {
            cabinetMeasurements: null,
            emptySpaceMeasurements: null,
        },
        rightWidth2Wall: null,
        leftDepthWall: null,
        rightDepthWall: null,
    };

    // Left Width Wall - cabinets facing right (currentDirection === 'right')
    if (walls.leftWidthWall) {
        const leftWidthWallConfig: MeasurementConfig = {
            wallHeight: lShapedDimension.height * scale + 20,
            defaultLabelFontSize,
            roomWidth: lShapedDimension.leftWidth,
            roomLength: lShapedDimension.leftDepth,
            leftWidth: lShapedDimension.leftWidth,
            rightWidth: lShapedDimension.rightWidth,
            leftDepth: lShapedDimension.leftDepth,
            rightDepth: lShapedDimension.rightDepth,
            scale,
            wallRefId: OBJECT_NAMES.LEFT_WIDTH_WALL,
            showArrows,
            isForPrinting,
            previewPosition,
        };

        result.leftWidthWall = createWallMeasurements(
            walls.leftWidthWall,
            OBJECT_NAMES.LEFT_WIDTH_WALL,
            'leftWidthWall',
            (cabinet) => {
                if (isPlanView) {
                    const {leftWidthWall} = walls;
                    const wallBox = new THREE.Box3().setFromObject(
                        leftWidthWall
                    );
                    const cabinetBox = new THREE.Box3().setFromObject(cabinet);

                    const minDistance = wallBox.max.x - cabinetBox.min.x;
                    const maxDistance = wallBox.max.x - cabinetBox.max.x;

                    const threshold = lShapedDimension.leftDepth * scale;
                    return (
                        !(Math.abs(minDistance) > threshold) &&
                        !(Math.abs(maxDistance) > threshold)
                    );
                } else if (isFrontElevation) {
                    return (
                        isCabinetClampedToWall(
                            cabinet,
                            walls.rightWidthWall,
                            'z'
                        ) &&
                        (getCachedDirection(cabinet) == 'front' ||
                            (getCachedDirection(cabinet) == 'right' &&
                                !!cabinet.userData?.isLShapedProduct))
                    );
                } else if (isLeftElevation) {
                    return (
                        isCabinetClampedToWall(
                            cabinet,
                            walls.leftWidthWall,
                            'x'
                        ) &&
                        (getCachedDirection(cabinet) == 'left' ||
                            (getCachedDirection(cabinet) == 'front' &&
                                !!cabinet.userData?.isLShapedProduct))
                    );
                }
                return (
                    getCachedDirection(cabinet) !== 'right' &&
                    isCabinetClampedToWall(cabinet, walls.leftWidthWall, 'x')
                );
            },
            leftWidthWallConfig
        );
    }

    // Left Width2 Wall - cabinets facing left (currentDirection === 'left')
    if (walls.leftWidth2Wall) {
        const leftWidth2WallConfig: MeasurementConfig = {
            wallHeight: lShapedDimension.height * scale + 20,
            defaultLabelFontSize,
            roomWidth:
                lShapedDimension.leftWidth -
                lShapedDimension.rightDepth -
                (isPlanView ? 25 : 0),
            roomLength: lShapedDimension.leftDepth,
            leftWidth: lShapedDimension.leftWidth,
            rightWidth: lShapedDimension.rightWidth,
            leftDepth: lShapedDimension.leftDepth,
            rightDepth: lShapedDimension.rightDepth,
            scale,
            wallRefId: OBJECT_NAMES.LEFT_WIDTH2_WALL,
            showArrows,
            isForPrinting,
            previewPosition,
        };
        const leftDepthWallLength =
            lShapedDimension.leftWidth - lShapedDimension.rightDepth;

        result.leftWidth2Wall = createWallMeasurements(
            walls.leftWidth2Wall,
            OBJECT_NAMES.LEFT_WIDTH2_WALL,
            // Own visibility key: piggybacking on leftWidthWall hid this
            // wall's measurements on the right elevation (LEFT_LEVELED),
            // where leftWidthWall is hidden but leftWidth2Wall must show.
            'leftWidth2Wall',
            (cabinet) => {
                if (isPlanView) {
                    const {leftDepthWall, leftWidthWall} = walls;
                    const leftDepth = new THREE.Box3().setFromObject(
                        leftDepthWall
                    );
                    const leftWidth = new THREE.Box3().setFromObject(
                        leftWidthWall
                    );
                    const cabinetBox = new THREE.Box3().setFromObject(cabinet);
                    const vertDistance = leftDepth.min.z - cabinetBox.max.z;
                    const horiDistance = leftWidth.max.x - cabinetBox.min.x;

                    const vertThreshold =
                        (lShapedDimension.leftWidth * scale) / 2;
                    const horiThreshold = lShapedDimension.leftDepth * scale;
                    return (
                        !(Math.abs(vertDistance) > vertThreshold) &&
                        !(Math.abs(horiDistance) > horiThreshold)
                    );
                } else if (isRightElevation) {
                    return (
                        isCabinetClampedToWall(
                            cabinet,
                            walls.leftWidth2Wall,
                            'x'
                        ) &&
                        (getCachedDirection(cabinet) == 'right' ||
                            (getCachedDirection(cabinet) == 'back' &&
                                !!cabinet.userData?.isLShapedProduct))
                    );
                }

                const boundingInfo = getCachedBoundingInfo(
                    cabinet,
                    leftWidth2WallConfig
                );

                const adjustedCabinetPositionZ =
                    boundingInfo.front + leftDepthWallLength;

                return (
                    getCachedDirection(cabinet) !== 'left' &&
                    adjustedCabinetPositionZ > leftDepthWallLength + 31.29 &&
                    isCabinetClampedToWall(cabinet, walls.leftWidth2Wall, 'x')
                );
            },
            leftWidth2WallConfig
        );
    }

    // Right Width Wall - cabinets facing left (currentDirection === 'left')
    if (walls.rightWidthWall) {
        const rightWidthWallConfig: MeasurementConfig = {
            wallHeight: lShapedDimension.height * scale + 20,
            defaultLabelFontSize,
            roomLength: lShapedDimension.rightWidth,
            roomWidth: lShapedDimension.leftWidth,
            leftWidth: lShapedDimension.leftWidth,
            rightWidth: lShapedDimension.rightWidth,
            leftDepth: lShapedDimension.leftDepth,
            rightDepth: lShapedDimension.rightDepth,
            scale,
            wallRefId: OBJECT_NAMES.RIGHT_WIDTH_WALL,
            showArrows,
            isForPrinting,
            previewPosition,
        };

        result.rightWidthWall = createWallMeasurements(
            walls.rightWidthWall,
            OBJECT_NAMES.RIGHT_WIDTH_WALL,
            'rightWidthWall',
            (cabinet) => {
                if (isPlanView) {
                    const {rightWidthWall} = walls;
                    const wallBox = new THREE.Box3().setFromObject(
                        rightWidthWall
                    );
                    const cabinetBox = new THREE.Box3().setFromObject(cabinet);

                    const minDistance = wallBox.max.z - cabinetBox.min.z;
                    const maxDistance = wallBox.max.z - cabinetBox.max.z;
                    const threshold = (lShapedDimension.leftWidth * scale) / 2;
                    return (
                        !(Math.abs(minDistance) > threshold) &&
                        !(Math.abs(maxDistance) > threshold)
                    );
                } else if (isFrontElevation) {
                    return (
                        isCabinetClampedToWall(
                            cabinet,
                            walls.rightWidthWall,
                            'z'
                        ) &&
                        (getCachedDirection(cabinet) == 'front' ||
                            (getCachedDirection(cabinet) == 'right' &&
                                !!cabinet.userData?.isLShapedProduct))
                    );
                } else if (isRightElevation) {
                    return (
                        (isCabinetClampedToWall(
                            cabinet,
                            walls.leftWidth2Wall,
                            'x'
                        ) ||
                            isCabinetClampedToWall(
                                cabinet,
                                walls.rightDepthWall,
                                'x'
                            )) &&
                        (getCachedDirection(cabinet) == 'right' ||
                            (getCachedDirection(cabinet) == 'back' &&
                                !!cabinet.userData?.isLShapedProduct))
                    );
                }
                return (
                    getCachedDirection(cabinet) !== 'back' &&
                    isCabinetClampedToWall(cabinet, walls.rightWidthWall, 'z')
                );
            },
            rightWidthWallConfig
        );
    }

    // Right Width2 Wall - cabinets facing right (currentDirection === 'right')
    if (walls.rightWidth2Wall) {
        const rightWidth2WallConfig: MeasurementConfig = {
            wallHeight: lShapedDimension.height * scale + 20,
            defaultLabelFontSize,
            roomWidth:
                lShapedDimension.rightWidth -
                lShapedDimension.leftDepth -
                (isPlanView ? 25 : 0),
            roomLength: lShapedDimension.leftWidth,
            leftWidth: lShapedDimension.leftWidth,
            rightWidth: lShapedDimension.rightWidth,
            leftDepth: lShapedDimension.leftDepth,
            rightDepth: lShapedDimension.rightDepth,
            scale,
            wallRefId: OBJECT_NAMES.RIGHT_WIDTH2_WALL,
            showArrows,
            isForPrinting,
            previewPosition,
        };

        const rightDepthWallLength =
            (lShapedDimension.rightWidth - lShapedDimension.leftDepth) * scale;

        result.rightWidth2Wall = createWallMeasurements(
            walls.rightWidth2Wall,
            OBJECT_NAMES.RIGHT_WIDTH2_WALL,
            // Own visibility key, same reason as leftWidth2Wall above.
            'rightWidth2Wall',
            (cabinet) => {
                if (isPlanView) {
                    const {rightDepthWall, leftDepthWall} = walls;
                    const leftDepth = new THREE.Box3().setFromObject(
                        leftDepthWall
                    );
                    const rightDepth = new THREE.Box3().setFromObject(
                        rightDepthWall
                    );
                    const cabinetBox = new THREE.Box3().setFromObject(cabinet);
                    const vertDistance = leftDepth.min.z - cabinetBox.max.z;
                    const horiDistance = rightDepth.max.x - cabinetBox.min.x;

                    const vertThreshold =
                        (lShapedDimension.leftWidth * scale) / 2;
                    const horiThreshold =
                        (lShapedDimension.rightWidth -
                            lShapedDimension.rightDepth) *
                        scale;
                    return (
                        !(Math.abs(vertDistance) > vertThreshold) &&
                        !(Math.abs(horiDistance) > horiThreshold + 30)
                    );
                }
                // OPTIMIZATION: Use cached direction
                const currentDirection = getCachedDirection(cabinet);

                const boundingInfo = getCachedBoundingInfo(
                    cabinet,
                    rightWidth2WallConfig
                );

                const adjustedCabinetPositionZ =
                    boundingInfo.left +
                    rightDepthWallLength -
                    (currentDirection === 'right' || currentDirection === 'left'
                        ? 18
                        : 0);
                return (
                    getCachedDirection(cabinet) !== 'front' &&
                    adjustedCabinetPositionZ > rightDepthWallLength &&
                    isCabinetClampedToWall(cabinet, walls.rightWidth2Wall, 'z')
                );
            },
            rightWidth2WallConfig
        );
    }

    if (walls.leftDepthWall) {
        const leftDepthWallConfig: MeasurementConfig = {
            wallHeight: lShapedDimension.height * scale + 20,
            defaultLabelFontSize,
            roomWidth: lShapedDimension.leftDepth,
            roomLength: lShapedDimension.leftWidth,
            leftWidth: lShapedDimension.leftWidth,
            rightWidth: lShapedDimension.rightWidth,
            leftDepth: lShapedDimension.leftDepth,
            rightDepth: lShapedDimension.rightDepth,
            scale,
            wallRefId: OBJECT_NAMES.LEFT_DEPTH_WALL,
            showArrows,
            isForPrinting,
            previewPosition,
        };

        const leftDepthWallLength = lShapedDimension.leftDepth * scale;

        result.leftDepthWall = createWallMeasurements(
            walls.leftDepthWall,
            OBJECT_NAMES.LEFT_DEPTH_WALL,
            'leftDepthWall',
            (cabinet) => {
                if (isPlanView) {
                    const {leftDepthWall, leftWidthWall} = walls;
                    const leftDepth = new THREE.Box3().setFromObject(
                        leftDepthWall
                    );
                    const leftWidth = new THREE.Box3().setFromObject(
                        leftWidthWall
                    );
                    const cabinetBox = new THREE.Box3().setFromObject(cabinet);
                    const vertDistance = leftDepth.min.z - cabinetBox.max.z;
                    const horiDistance = leftWidth.max.x - cabinetBox.min.x;

                    const vertThreshold =
                        (lShapedDimension.leftWidth * scale) / 2;
                    const horiThreshold = lShapedDimension.leftDepth * scale;
                    return (
                        !(Math.abs(vertDistance) > vertThreshold) &&
                        !(Math.abs(horiDistance) > horiThreshold)
                    );
                } else if (isLeftElevation) {
                    return (
                        isCabinetClampedToWall(
                            cabinet,
                            walls.leftWidthWall,
                            'x'
                        ) &&
                        (getCachedDirection(cabinet) == 'left' ||
                            (getCachedDirection(cabinet) == 'front' &&
                                !!cabinet.userData?.isLShapedProduct))
                    );
                }
                const boundingInfo = getCachedBoundingInfo(
                    cabinet,
                    leftDepthWallConfig
                );

                const adjustedCabinetPositionX =
                    boundingInfo.center.x +
                    leftDepthWallLength -
                    boundingInfo.size.width;

                return (
                    getCachedDirection(cabinet) !== 'front' &&
                    adjustedCabinetPositionX < leftDepthWallLength &&
                    isCabinetClampedToWall(cabinet, walls.leftDepthWall, 'z')
                );
            },
            leftDepthWallConfig
        );
    }

    // // Right Depth Wall - cabinets facing front (currentDirection === 'front')
    if (walls.rightDepthWall) {
        const rightDepthWallConfig: MeasurementConfig = {
            wallHeight: lShapedDimension.height * scale + 20,
            defaultLabelFontSize,
            roomWidth: lShapedDimension.leftWidth,
            roomLength: lShapedDimension.leftDepth,
            leftWidth: lShapedDimension.leftWidth,
            rightWidth: lShapedDimension.rightWidth,
            leftDepth: lShapedDimension.leftDepth,
            rightDepth: lShapedDimension.rightDepth,
            scale,
            wallRefId: OBJECT_NAMES.RIGHT_DEPTH_WALL,
            showArrows,
            isForPrinting,
            previewPosition,
        };

        // Compute the valid range for rightDepthWall (Z axis, with adjustment for L-shaped rooms)

        const rightDepthWallLength = lShapedDimension.rightDepth * scale;

        result.rightDepthWall = createWallMeasurements(
            walls.rightDepthWall,
            OBJECT_NAMES.RIGHT_DEPTH_WALL,
            'rightDepthWall',
            (cabinet) => {
                if (isPlanView) {
                    const {rightDepthWall} = walls;
                    const wallBox = new THREE.Box3().setFromObject(
                        rightDepthWall
                    );
                    const cabinetBox = new THREE.Box3().setFromObject(cabinet);

                    const distance = wallBox.min.x - cabinetBox.max.x;
                    const threshold =
                        (lShapedDimension.rightWidth -
                            lShapedDimension.leftDepth) *
                        scale;
                    const withinX = !(Math.abs(distance) > threshold - 25);
                    // The wall only spans down to the inner corner. A cabinet
                    // past that corner (in the notch region) is not alongside
                    // this wall — measuring it here prints its gap/width row
                    // beyond the wall's end, outside the room.
                    const overlapsWallSpan =
                        cabinetBox.min.z < wallBox.max.z + 25 &&
                        cabinetBox.max.z > wallBox.min.z - 25;
                    return withinX && overlapsWallSpan;
                } else if (isRightElevation) {
                    return (
                        isCabinetClampedToWall(
                            cabinet,
                            walls.rightDepthWall,
                            'x'
                        ) &&
                        (getCachedDirection(cabinet) == 'right' ||
                            (getCachedDirection(cabinet) == 'back' &&
                                !!cabinet.userData?.isLShapedProduct))
                    );
                }
                const adjustedCabinetPositionZ =
                    cabinet.position.z + rightDepthWallLength;
                return (
                    getCachedDirection(cabinet) !== 'left' &&
                    adjustedCabinetPositionZ < rightDepthWallLength + 25 &&
                    isCabinetClampedToWall(cabinet, walls.rightDepthWall, 'x')
                );
            },
            rightDepthWallConfig
        );
    }

    return result;
};

/**
 * Conditionally shows or hides wall lines based on printing mode
 * @param {THREE.Group} wallGroup - The wall group containing line elements
 * @param {boolean} isForPrinting - Whether the view is for printing
 * @param {boolean} override - Whether to override the printing visibility rules
 * @return {void}
 */
export function conditionallyShowWallLines(
    wallGroup: THREE.Group,
    isForPrinting: boolean,
    override = false
) {
    wallGroup?.traverse((child) => {
        if (
            child instanceof THREE.Line ||
            child instanceof THREE.LineSegments ||
            child instanceof THREE.Sprite ||
            child instanceof THREE.ArrowHelper
        ) {
            child.visible = !isForPrinting || override;
        }
    });
}

/**
 * Sets up measurements for room walls and cabinets based on room type
 * @param {React.MutableRefObject<THREE.Mesh[]>} wallRefs - Reference to wall mesh objects
 * @param {THREE.Group<THREE.Object3DEventMap>[] | undefined} cabinetGroupList - Array of cabinet group objects
 * @param {Function} handleArrowsAndGroup - Function to handle arrow and group creation
 * @param {Function} getGroupWidth - Function to get width, height, and depth of a group
 * @param {string} roomType - Type of room (RECTANGULAR or LSHAPED)
 * @param {Object} rectangularDimension - Dimensions for rectangular room
 * @param {number} rectangularDimension.length - Length of rectangular room
 * @param {number} rectangularDimension.width - Width of rectangular room
 * @param {number} rectangularDimension.height - Height of rectangular room
 * @param {Object} lShapedDimension - Dimensions for L-shaped room
 * @param {number} lShapedDimension.leftWidth - Left width of L-shaped room
 * @param {number} lShapedDimension.rightWidth - Right width of L-shaped room
 * @param {number} lShapedDimension.leftDepth - Left depth of L-shaped room
 * @param {number} lShapedDimension.rightDepth - Right depth of L-shaped room
 * @param {number} lShapedDimension.height - Height of L-shaped room
 * @param {number} scale - Scale factor for measurements
 * @param {boolean} showCabinets - Whether to show cabinets
 * @param {boolean} showArrows - Whether to show measurement arrows
 * @param {THREE.Group<THREE.Object3DEventMap>} cabinetGroup - Current cabinet group being processed
 * @param {String} defaultLabelFontSize - Default Font
 * @param {boolean} isForPrinting - Whether the view is for printing
 * @param {PreviewPosition} previewPosition - Current preview position
 * @param {string | null} hiddenWall - Comma-separated string of hidden wall IDs (optional)
 * @param {boolean} skipCleanup - Skip removing existing measurements (used when accumulating print views)
 * @return {RectangularMeasurementGroups | LShapedMeasurementGroups | null} Measurement groups for the room type or null if setup fails
 */
export const setupMeasurements = (
    wallRefs: React.MutableRefObject<THREE.Mesh[]>,
    cabinetGroupList: THREE.Group<THREE.Object3DEventMap>[] | undefined,
    handleArrowsAndGroup: (
        arrows: (THREE.ArrowHelper | THREE.SpriteMaterial)[],
        arrowGroup?: THREE.Group
    ) => void,
    getGroupWidth: (group: THREE.Group<THREE.Object3DEventMap>) => {
        width: number;
        height: number;
        depth: number;
    },
    roomType: string,
    rectangularDimension: {
        length: number;
        width: number;
        height: number;
    },
    lShapedDimension: {
        leftWidth: number;
        rightWidth: number;
        leftDepth: number;
        rightDepth: number;
        height: number;
    },
    scale: number,
    showCabinets: boolean,
    showArrows: boolean,
    cabinetGroup: THREE.Group<THREE.Object3DEventMap>,
    defaultLabelFontSize: string,
    isForPrinting: boolean,
    previewPosition: PreviewPosition,
    hiddenWall?: string | null,
    skipCleanup = false
): RectangularMeasurementGroups | LShapedMeasurementGroups | null => {
    // Continue even without cabinets to show wall measurements
    if (isForPrinting && !previewPosition) return null;

    if (roomType === 'RECTANGULAR') {
        // Handle rectangular room measurements using optimized data-driven approach
        const measurements = setupOptimizedMeasurements(
            wallRefs,
            cabinetGroupList,
            handleArrowsAndGroup,
            getGroupWidth,
            rectangularDimension,
            scale,
            showCabinets,
            showArrows,
            cabinetGroup,
            defaultLabelFontSize,
            isForPrinting,
            previewPosition,
            hiddenWall,
            skipCleanup
        );

        if (measurements && isForPrinting) {
            const walls: {
                key: keyof RectangularMeasurementGroups;
                value: WallMeasurements;
            }[] = [
                {key: 'backWall', value: measurements.backWall},
                {key: 'frontWall', value: measurements.frontWall},
                {key: 'leftWall', value: measurements.leftWall},
                {key: 'rightWall', value: measurements.rightWall},
            ];

            const sets = canShowLabelAndMeasurements(
                isForPrinting,
                previewPosition
            );

            for (const {key, value} of walls) {
                if (value) {
                    const {cabinetMeasurements, emptySpaceMeasurements} = value;
                    if (
                        ![
                            'TOP',
                            'FRONT',
                            'LEFT_LEVELED',
                            'RIGHT_LEVELED',
                        ].includes(previewPosition)
                    ) {
                        preventSpriteOverlapByView(
                            key,
                            cabinetMeasurements,
                            emptySpaceMeasurements,
                            60,
                            previewPosition
                        );
                    }

                    conditionallyShowWallLines(
                        cabinetMeasurements,
                        // eslint-disable-next-line security/detect-object-injection -- Object key is from a predefined configuration object, not user input
                        !sets[key]
                    );
                    conditionallyShowWallLines(
                        emptySpaceMeasurements,
                        // eslint-disable-next-line security/detect-object-injection -- Object key is from a predefined configuration object, not user input
                        !sets[key]
                    );
                }
            }
        }

        return measurements;
    } else if (roomType === 'LSHAPED') {
        // Handle L-shaped room measurements
        const measurements = setupLShapedMeasurements(
            wallRefs,
            cabinetGroupList,
            handleArrowsAndGroup,
            getGroupWidth,
            lShapedDimension,
            scale,
            showCabinets,
            showArrows,
            cabinetGroup,
            defaultLabelFontSize,
            isForPrinting,
            previewPosition,
            hiddenWall,
            skipCleanup
        );

        if (measurements && isForPrinting) {
            const walls: {
                key:
                    | 'rightWidthWall'
                    | 'leftWidthWall'
                    | 'leftDepthWall'
                    | 'rightDepthWall'
                    | 'rightWidth2Wall'
                    | 'leftWidth2Wall';
                value: WallMeasurements;
            }[] = [
                {key: 'rightWidthWall', value: measurements['rightWidthWall']},
                {key: 'leftWidthWall', value: measurements['leftWidthWall']},
                {key: 'leftDepthWall', value: measurements['leftDepthWall']},
                {key: 'rightDepthWall', value: measurements['rightDepthWall']},
                {
                    key: 'rightWidth2Wall',
                    value: measurements['rightWidth2Wall'],
                },
                {key: 'leftWidth2Wall', value: measurements['leftWidth2Wall']},
            ];

            const toggleWallViews = canShowLabelAndMeasurements(
                isForPrinting,
                previewPosition
            );

            for (const {key, value} of walls) {
                if (value) {
                    if (
                        ![
                            'TOP',
                            'FRONT',
                            'LEFT_LEVELED',
                            'RIGHT_LEVELED',
                        ].includes(previewPosition)
                    ) {
                        preventLShapedSpriteOverlapByView(
                            key,
                            value.cabinetMeasurements,
                            value.emptySpaceMeasurements,
                            60,
                            previewPosition
                        );
                    }

                    conditionallyShowWallLines(
                        value.cabinetMeasurements,
                        // eslint-disable-next-line security/detect-object-injection -- Object key is from a predefined configuration object, not user input
                        !toggleWallViews[key],
                        false
                    );
                    conditionallyShowWallLines(
                        value.emptySpaceMeasurements,
                        // eslint-disable-next-line security/detect-object-injection -- Object key is from a predefined configuration object, not user input
                        !toggleWallViews[key],
                        false
                    );
                }
            }
        }

        return measurements;
    }

    return null;
};
